import {
  BudgetFriendly,
  ChooseADay, Customizable,
  ExclusiveDates,
  Group,
  Lux,
  MidLux,
  Private,
  Sharable,
  SoloFriendly,
  SoloRequest,
  HighEnd
} from "@/components/icons";
import { EFeatureKey } from "@/enums";
import { ITourFeatureCard } from "../TourFeatureCard";

type TAllFeatures = Record<EFeatureKey, ITourFeatureCard>;

export const useFeatures = ({ size = 48, color = "#B2B2B2" }: { size?: number; color?: string }): { getFeatureDetail: (key: string) => ITourFeatureCard | undefined } => {
  const allFeatures: TAllFeatures = {
    [EFeatureKey.GROUPONLY]: {
      icon: <Group size={size.toString()} color={color} />,
      title: "For groups only",
      description:
        <>
          This private tour is best enjoyed with two or more people,
          <br />
          so it&apos;s not available for solo travelers.
        </>
    },
    [EFeatureKey.STARTWHENEVER]: {
      icon: <ChooseADay size={size.toString()} color={color} />,
      title: "Start whenever you like",
      description: "If dates are open, you can kick off your tour on any day that works for you."
    },
    [EFeatureKey.PRIVATE]: {
      icon: <Private size={size.toString()} color={color} />,
      title: "Just for you",
      description:
        <>
          Your tour will be completely private,
          <br />
          planned only for your group - no sharing with strangers.
        </>
    },
    [EFeatureKey.SOLOFRIENDLY]: {
      icon: <SoloFriendly size={size.toString()} color={color} />,
      title: "Solo traveler friendly",
      description:
        <>
          Perfect for solo explorers. Join a group, meet fellow travelers,
          <br />
          and enjoy a safe, welcoming adventure on your own schedule.
        </>
    },
    [EFeatureKey.SOLOREQUEST]: {
      icon: <SoloRequest size={size.toString()} color={color} />,
      title: "Solo travelers on request",
      description:
        <>
          Solo travelers are welcome on selected departures or with a private arrangement.
          <br />
          Contact us to check availability and pricing.
        </>
    },
    [EFeatureKey.CUSTOMIZE]: {
      icon: <Customizable size={size.toString()} color={color} />,
      title: "A trip that fits you",
      description:
        <>
          We&apos;re happy to make small tweaks to the route
          <br />
          or stays so the journey feels more personal.
        </>
    },
    [EFeatureKey.LUX]: {
      icon: <Lux size={size.toString()} color={color} />,
      title: "Lux tour",
      description:
        <>
          Premium safari experience with high-end lodges, private guides,
          <br />
          and exceptional comfort throughout your journey.
        </>
    },
    [EFeatureKey.MIDLUX]: {
      icon: <MidLux size={size.toString()} color={color} />,
      title: "Mid-range",
      description:
        <>
          Handpicked lodges and camps in prime wildlife locations.
          <br />
          Comfortable stays with attentive service throughout the journey.
        </>
    },
    [EFeatureKey.BUDGETFRIENDLY]: {
      icon: <BudgetFriendly size={size.toString()} color={color} />,
      title: "Budget friendly",
      description:
        <>
          Thoughtfully curated safari experience with comfortable camps and lodges
          <br />
          that keep costs low without compromising adventure.
        </>
    },
    [EFeatureKey.EXCLUSIVEDATES]: {
      icon: <ExclusiveDates size={size.toString()} color={color} />,
      title: "Exclusive dates only",
      description:
        <>
          Limited-date departure for an intimate, curated journey.
          <br />
          Available only on select dates.
        </>
    },
    [EFeatureKey.SHARABLE]: {
      icon: <Sharable size={size.toString()} color={color} />,
      title: "Shared tour",
      description:
        <>
          Join a small group of fellow travelers to share the adventure.
          <br />
          A social and cost-efficient way to experience safari.
        </>
    },
    [EFeatureKey.HIGHEND]: {
      icon: <HighEnd size={size.toString()} color={color} />,
      title: "Hign-end",
      description:
        <>
          Ultimate safari experience with high-end lodges,
          <br />
          private guides, and exceptional comfort throughout your journey.
        </>
    }
  };

  function isFeatureKey(value: string): value is EFeatureKey {
    return Object.values(EFeatureKey).includes(value as EFeatureKey);
  }


  function getFeatureDetail(key: string): ITourFeatureCard | undefined {
    if (isFeatureKey(key)) {
      return allFeatures[key];
    }
    return undefined;
  }

  return {
    getFeatureDetail
  }
}